import type { Award, Dcfh2Item, Me, Opportunity, PipelineItem, PipelineStatus, Scan, ScanResult } from '../lib/types'

/** Thrown on 401 — the session expired or was never established. */
export class UnauthorizedError extends Error {
  constructor() {
    super('Your session has expired — sign in again to continue.')
  }
}

/** Carries the backend's ProblemDetail `detail`, which is written to be shown to the user. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
  }
}

/**
 * Called when any request other than /api/auth/me comes back 401. The prototype had no
 * sessions at all; here a session can expire mid-use, and the kindest handling is to drop
 * the whole app back behind the sign-in gate rather than let every view fail one by one.
 * useAuth registers the handler.
 */
type UnauthorizedHandler = () => void
let unauthorizedHandler: UnauthorizedHandler | null = null
export function onUnauthorized(handler: UnauthorizedHandler): void {
  unauthorizedHandler = handler
}

/**
 * Spring writes the CSRF token to a JS-readable XSRF-TOKEN cookie; we echo it back in a
 * header on every mutating request. The session cookie itself stays HttpOnly and is never
 * touched from here — that is the whole point of the BFF pattern.
 */
function csrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/)
  return match ? decodeURIComponent(match[1]) : null
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = csrfToken()
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { 'X-XSRF-TOKEN': token } : {}),
      ...init.headers,
    },
  })

  if (res.status === 401) {
    if (path !== '/api/auth/me') unauthorizedHandler?.()
    throw new UnauthorizedError()
  }

  if (!res.ok) {
    // The backend returns RFC 7807 ProblemDetail; `detail` is the user-facing message.
    const problem = await res.json().catch(() => null)
    throw new ApiError(problem?.detail ?? `Request failed (${res.status})`, res.status)
  }

  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export const api = {
  me: () => request<Me>('/api/auth/me'),

  opportunities: () => request<Opportunity[]>('/api/opportunities'),

  deleteOpportunity: (id: string) =>
    request<void>(`/api/opportunities/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  pipeline: () => request<PipelineItem[]>('/api/pipeline'),

  pursue: (opportunityId: string) =>
    request<PipelineItem>('/api/pipeline', { method: 'POST', body: JSON.stringify({ opportunityId }) }),

  setPipelineStatus: (id: string, status: PipelineStatus) =>
    request<PipelineItem>(`/api/pipeline/${encodeURIComponent(id)}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    }),

  removeFromPipeline: (id: string) =>
    request<void>(`/api/pipeline/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /** The billed Claude call — the whole scan pipeline runs server-side now. */
  scan: (raw: string) => request<ScanResult>('/api/scans', { method: 'POST', body: JSON.stringify({ raw }) }),

  scans: () => request<Scan[]>('/api/scans'),

  awards: () => request<Award[]>('/api/awards'),

  /** Curation, same pattern as opportunities: the server enforces the ADMIN role. */
  deleteAward: (id: string) =>
    request<void>(`/api/awards/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  dcfh2: () => request<Dcfh2Item[]>('/api/dcfh2'),
}
